import {Divider, Flex, Select, Table, DatePicker, TableProps} from 'antd';
import type {RangePickerProps} from 'antd/es/date-picker';
import Title from 'antd/es/typography/Title';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import Loader from '../../components/Loader/Loader';
import PageContent from '../../components/PageContent/PageContent';
import activityLogApi from '../../services/modules/ActivityLog/activityLogDetails';
import activityTypeApi from '../../services/modules/ActivityLog/activityTypeDetails';
import activityUsersDetails from '../../services/modules/ActivityLog/activityUsersDetails';
import useJSONStore from '../../store/StaticJson/jsonStore';

import {
  ActivityLogData,
  DataType,
  Filters,
  User
} from './interface/activityLog';

const ActivityLog: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activityLogData, setActivityLogData] = useState<ActivityLogData>({
    data: [],
    total: 0
  });
  const {activityTypes} = useJSONStore.getState();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [filters, setFilters] = useState<Filters>({
    userId: null,
    action: null,
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    const fetchActivityDetails = async () => {
      setIsLoading(true);
      try {
        const res = await activityLogApi({
          ...filters,
          page: pagination.current,
          pageSize: pagination.pageSize
        });
        setActivityLogData({data: res?.data ?? [], total: res?.total ?? 0});
      } catch (error) {
        setActivityLogData({
          data: [],
          total: 0
        });
      } finally {
        setIsLoading(false);
      }
    };

    void fetchActivityDetails();
  }, [filters, pagination.current, pagination.pageSize]);

  const handleDateChange: RangePickerProps['onChange'] = (values) => {
    const formattedDates =
      values?.map((date) => date?.format('YYYY-MM-DD')) ?? [];
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: 10
    }));
    setFilters((prev) => ({
      ...prev,
      startDate: formattedDates?.[0] ?? null,
      endDate: formattedDates?.[1] ?? null
    }));
  };

  const handleUserChange = (userId: string) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: 10
    }));
    setFilters((prev) => ({...prev, userId}));
  };

  const handleActivityTypeChange = (
    value: string | number | undefined,
    option: any
  ) => {
    const action = option?.label || null;
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: 10
    }));
    setFilters((prev) => ({...prev, action}));
  };

  const handleTableChange = (paginationInfo: any) => {
    const {current, pageSize} = paginationInfo;
    setPagination((prev) => ({
      ...prev,
      current,
      pageSize
    }));
  };

  const columns: TableProps<DataType>['columns'] = [
    {
      title: 'Date/Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (value) => moment(value).format('DD/MM/YYYY hh:mm A')
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action, record) =>
        `${action}${record.activity ? `: ${record.activity}` : ''}`
    },
    {
      title: 'Action Taken By',
      dataIndex: 'actionTaken',
      key: 'actionTaker'
    },
    {
      title: 'Description',
      key: 'desc',
      dataIndex: 'description'
    }
  ];

  useEffect(() => {
    activityUsersDetails()
      .then((res1) => {
        setUsers(res1 ?? []);
      })
      .catch(() => {
        setUsers([]);
      });
  }, []);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: activityLogData?.total
    }));
  }, [activityLogData.total]);

  useEffect(() => {
    void activityTypeApi();
  }, []);

  return (
    <PageContent>
      <Flex vertical className="activity-log">
        <Flex align="center" justify="space-between">
          <Title level={4} className="mainHeader">
            Activity Log
          </Title>
          <Flex gap={10}>
            <Select
              defaultValue={undefined}
              onChange={handleUserChange}
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Select a user"
              style={{width: 150}}
              filterOption={(input, option) => {
                return (
                  option?.label.toLowerCase().includes(input.toLowerCase()) ??
                  false
                );
              }}
              options={
                users?.map((user) => ({
                  value: user._id,
                  label: `${user.firstName} ${user.lastName}`
                })) ?? []
              }
            />

            <Select
              defaultValue={undefined}
              onChange={handleActivityTypeChange}
              options={
                activityTypes?.map((item) => ({
                  value: item.value,
                  label: item.label
                })) ?? []
              }
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) => {
                return option?.label
                  .toLowerCase()
                  .includes(input.toLowerCase());
              }}
              placeholder="Select Type"
              style={{width: 150}}
            />

            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              onChange={handleDateChange}
              disabledDate={(current) =>
                current && current > moment().endOf('day')
              }
            />
          </Flex>
        </Flex>
        <Divider />
        {isLoading ? (
          <Loader />
        ) : (
          <Table
            dataSource={activityLogData.data}
            columns={columns}
            loading={{
              spinning: isLoading,
              indicator: <Loader />
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total
            }}
            onChange={handleTableChange}
            rowKey="_id"
          />
        )}
      </Flex>
    </PageContent>
  );
};

export default ActivityLog;
